import { useEffect, useRef, useState } from 'react';

import { faSparkles } from '@awesome.me/kit-f3dc60f0d0/icons/classic/regular';
import {
  faExclamationTriangle,
  faMicrophoneSlash,
  faPause,
  faPlay,
  faXmark,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@repo/videa-components/src/index';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';

import { createDebugLogger } from '@videa/utils';

import { eventTrackerAtom } from '../../atoms/eventTracker';
import {
  accumulatedDurationAtom,
  isReconstructingBlobAtom,
  recordingBlockedByOtherTabAtom,
  isRecordingSessionActiveAtom,
  isRecordingSessionPausedAtom,
  isSilentAtom,
  mediaBlobUrlAtom,
  monitoringStreamAtom,
  previewAudioStreamAtom,
  recorderStatusAtom,
  recordingStartTimeAtom,
  silenceSeverityAtom,
  uploadedAudioFileAtom,
} from '../../atoms/recording';
import {
  isCancelRecordingConfirmationShownAtom,
  isRecorderSheetOpenAtom,
  shouldReopenRecorderSheetAtom,
} from '../../atoms/ui';
import { useMicrophoneContext } from '../../contexts/MicrophoneContext';
import { buttonStyling } from '../../styles';
import type { MicrophonePermissionOutcome } from '../permissions/MicrophonePermissionFlow';
import { MicrophonePermissionFlow } from '../permissions/MicrophonePermissionFlow';
import { SheetFooter } from '../SheetFooter';
import { TimerDisplay } from '../TimerDisplay';
import { UserInstructions } from '../UserInstructions';

import { getAudioRecorder } from './audioRecorderControls';
import { ConsentFlow } from './ConsentFlow';
import { VoiceCard } from './VoiceCard';

const debug = createDebugLogger('AudioRecorder');

interface AudioRecorderProps {
  onCancelRecording: () => void;
  onCancelRecordingConfirmation: () => void;
  onPauseRecording: () => void;
  onResumeRecording: () => void;
  onStartRecording: () => void;
  onStartProviderOnlyRecording: () => void;
  onGenerateNote: (uploadedFile?: File, blobUrl?: string) => void;
  onPatientConsentDenied: () => void;
  onPermissionOutcome?: (outcome: MicrophonePermissionOutcome) => void;
  isTemplateSelected: boolean;
  // Dependencies injected from consuming app
  patientName?: string;
  patientId?: string;
  enableVoiceNotesConsent?: boolean;
  // Optional styling props for extension customization
  microphonePermissionFlowProps?: {
    textClassName?: string;
    buttonClassName?: string;
    containerClassName?: string;
  };
  voiceCardProps?: {
    iconSize?: 'sm' | 'md' | 'lg';
  };
  /** When false, the mic visualizer (VoiceCard) is hidden. Use during template selection to show template chips instead. */
  showVisualizer?: boolean;
  isVoiceNotesConsentEnabled?: boolean;
  isAudioUploadEnabled?: boolean;
  showDevTools?: boolean;
  compactMode?: boolean; // Stack consent buttons vertically for narrow UIs
  templateName?: string | null;
  templateUserInstructions?: string | null;
  /** Optional classNames for UserInstructions (e.g. extension uses for full width + list styling). */
  userInstructionsWrapperClassName?: string;
  userInstructionsContentClassName?: string;
  /** When true, MicrophonePermissionFlow is rendered externally (e.g. above templates) so AudioRecorder skips it. */
  externalPermissionFlow?: boolean;
}

export const AudioRecorder = ({
  onCancelRecording,
  onCancelRecordingConfirmation,
  onPauseRecording,
  onResumeRecording,
  onStartRecording,
  onStartProviderOnlyRecording,
  onGenerateNote,
  onPatientConsentDenied,
  onPermissionOutcome,
  isTemplateSelected,
  microphonePermissionFlowProps,
  voiceCardProps,
  showVisualizer = true,
  isVoiceNotesConsentEnabled = false,
  isAudioUploadEnabled = false,
  showDevTools = false,
  compactMode = false,
  templateName,
  templateUserInstructions,
  userInstructionsWrapperClassName,
  userInstructionsContentClassName,
  externalPermissionFlow = false,
}: AudioRecorderProps) => {
  // atoms
  const isRecordingSessionActive = useAtomValue(isRecordingSessionActiveAtom);
  const isRecordingSessionPaused = useAtomValue(isRecordingSessionPausedAtom);
  const mediaBlobUrl = useAtomValue(mediaBlobUrlAtom);
  const setMediaBlobUrl = useSetAtom(mediaBlobUrlAtom);
  const setUploadedAudioFile = useSetAtom(uploadedAudioFileAtom);
  const previewAudioStream = useAtomValue(previewAudioStreamAtom);
  const [isSheetOpen, setIsSheetOpen] = useAtom(isRecorderSheetOpenAtom);
  const [shouldReopen, setShouldReopen] = useAtom(
    shouldReopenRecorderSheetAtom,
  );
  const [
    isCancelRecordingConfirmationShown,
    setIsCancelRecordingConfirmationShown,
  ] = useAtom(isCancelRecordingConfirmationShownAtom);
  const monitoringStream = useAtomValue(monitoringStreamAtom);
  const recorderStatus = useAtomValue(recorderStatusAtom);
  const isReconstructingBlob = useAtomValue(isReconstructingBlobAtom);
  const isSilent = useAtomValue(isSilentAtom);
  const recordingBlockedByOtherTab = useAtomValue(recordingBlockedByOtherTabAtom);
  const silenceSeverity = useAtomValue(silenceSeverityAtom);
  const eventTracker = useAtomValue(eventTrackerAtom);
  const accumulatedDuration = useAtomValue(accumulatedDurationAtom);
  const recordingStartTime = useAtomValue(recordingStartTimeAtom);
  const pauseStartedAtRef = useRef<number | null>(null);
  // Permission state comes from MicrophoneContext (browser API source of truth)
  const { hasPermission: hasMicrophonePermission } = useMicrophoneContext();

  // audio recording control interface
  const startRecording = () => getAudioRecorder().start?.();
  const stopRecording = () => getAudioRecorder().stop?.();
  const pauseRecording = () => getAudioRecorder().pause?.();
  const resumeRecording = () => getAudioRecorder().resume?.();
  const clearRecording = (deleteOpfsSession = true) =>
    getAudioRecorder().clear?.(deleteOpfsSession);

  // local state
  const [isRecordingStopped, setIsRecordingStopped] = useState(false);
  const [isRecordingCanceled, setIsRecordingCanceled] = useState(false);

  const displayNoticeText = () => {
    if (!isRecordingSessionActive) return null;
    if (isCancelRecordingConfirmationShown) {
      return 'Recording paused - waiting for confirmation to cancel.';
    }
    if (isRecordingSessionPaused) {
      return 'Audio capture is paused.';
    }
    return 'Capturing audio...';
  };

  const displayNoticeDetailsText = () => {
    if (!isRecordingSessionActive) return null;
    if (isCancelRecordingConfirmationShown) {
      return "If you cancel now, you'll lose all progress on this recording.";
    }
    if (isRecordingSessionPaused) {
      return 'Resume recording when you are ready to continue.';
    }
    return "When you're done speaking, select Generate Note.";
  };

  const handleStartRecording = () => {
    try {
      startRecording();
      onStartRecording();
    } catch (err) {
      debug.error('Failed to start recording:', err);
    }
  };

  const handleStartProviderOnlyRecording = () => {
    try {
      startRecording();
      onStartProviderOnlyRecording();
    } catch (err) {
      debug.error('Failed to start recording:', err);
    }
  };

  /**
   * This pauses the active recording session, and toggles on
   * the "cancel confirmation" button set in place of the default
   * audio recorder controls.
   */
  const handleCancelButtonClick = () => {
    pauseRecording();
    onCancelRecording();
    setIsCancelRecordingConfirmationShown(true);
  };

  /**
   * This is effective the "go back" action if a user does not want
   * to commit to actually canceling the active recording.
   */
  const handleNotNowButtonClick = () => {
    resumeRecording();
    setIsCancelRecordingConfirmationShown(false);
  };

  const handleResumeRecording = () => {
    const pauseDurationSeconds =
      pauseStartedAtRef.current != null
        ? Math.floor((Date.now() - pauseStartedAtRef.current) / 1000)
        : undefined;
    eventTracker.track(
      'recording_resumed',
      pauseDurationSeconds != null
        ? { pause_duration_seconds: pauseDurationSeconds }
        : undefined,
    );
    pauseStartedAtRef.current = null;
    resumeRecording();
    onResumeRecording();
  };

  const handlePauseRecording = () => {
    const elapsedSeconds =
      accumulatedDuration +
      (recordingStartTime != null
        ? Math.floor((Date.now() - recordingStartTime) / 1000)
        : 0);
    eventTracker.track('recording_paused', {
      elapsed_seconds: elapsedSeconds,
    });
    pauseStartedAtRef.current = Date.now();
    pauseRecording();
    onPauseRecording();
  };

  /**
   * This stops recording audio via MediaStream Recording API.
   * As soon as `stopRecording()` finishes running, the value of `mediaBlobUrl`
   * should no longer be `undefined`, which will bubble back up to the parent.
   */
  const handleStopRecording = () => {
    setIsRecordingStopped(true);
    setIsRecordingCanceled(false);
    stopRecording();
  };

  /**
   * This is the "cancel confirmation" action, which stops the active
   * recording, fires off an async request to clear the mediaBlobUrl,
   * hides the "cancel confirm" button set and invokes the onCancelRecording
   * callback.
   */
  const handleCancelRecordingConfirmationButtonClick = () => {
    const elapsedSeconds =
      accumulatedDuration +
      (recordingStartTime != null
        ? Math.floor((Date.now() - recordingStartTime) / 1000)
        : 0);
    eventTracker.track('recording_canceled', {
      elapsed_seconds: elapsedSeconds,
    });

    setIsRecordingStopped(true);
    setIsRecordingCanceled(true);
    setIsCancelRecordingConfirmationShown(false);
    stopRecording();
    if (!isSheetOpen) {
      setShouldReopen(true);
    }
  };

  const handleConsentDeniedClick = () => {
    setIsSheetOpen(false);
    onPatientConsentDenied();
  };

  /**
   * Handles audio file upload. Sets the blob URL and uploaded file,
   * then triggers note generation.
   */
  const handleAudioFileSelected = (file: File, blobUrl: string) => {
    // Store the uploaded file so it can be used directly during upload
    setUploadedAudioFile(file);
    // Set the blob URL to trigger the note generation flow
    setMediaBlobUrl(blobUrl);
    // Pass file and blobUrl directly to avoid closure issues
    // where handleSave would see stale atom values
    onGenerateNote(file, blobUrl);
  };

  // store the latest version of onGenerateNote in a ref
  const onGenerateNoteRef = useRef(onGenerateNote);
  const onCancelRecordingRef = useRef(onCancelRecording);
  const onCancelRecordingConfirmationRef = useRef(
    onCancelRecordingConfirmation,
  );

  useEffect(() => {
    onGenerateNoteRef.current = onGenerateNote;
  }, [onGenerateNote]);

  useEffect(() => {
    onCancelRecordingRef.current = onCancelRecording;
  }, [onCancelRecording]);

  useEffect(() => {
    onCancelRecordingConfirmationRef.current = onCancelRecordingConfirmation;
  }, [onCancelRecordingConfirmation]);

  useEffect(() => {
    if (shouldReopen) {
      setIsSheetOpen(true);
      setShouldReopen(false);
    }
  }, [shouldReopen, setIsSheetOpen, setShouldReopen]);

  useEffect(() => {
    if (!isRecordingStopped) return;

    if (isRecordingCanceled) {
      if (mediaBlobUrl !== undefined) {
        clearRecording(true); // Delete OPFS session on cancel
        onCancelRecordingConfirmationRef.current();
        setIsRecordingStopped(false);
        setIsRecordingCanceled(false);
      }
    } else if (mediaBlobUrl !== undefined) {
      onGenerateNoteRef.current();
      // Don't delete OPFS session here - handleRecordingComplete may still need it
      // for blob reconstruction. Session cleanup happens after successful upload.
      clearRecording(false);
      setIsRecordingStopped(false);
    } else if (recorderStatus === 'stopped' && !isReconstructingBlob) {
      // Recording stopped but no audio data was captured (dead stream, device
      // error, etc.). Without this branch the recorder stays in 'stopped' status
      // forever, which prevents MicrophoneSelector from restarting monitoring
      // and leaves the "Start Recording" button in an infinite loading state.
      //
      // Guard: skip when isReconstructingBlob is true — a resumed recording
      // sets recorderStatus to 'stopped' before the async OPFS blob
      // reconstruction completes. Without this guard, we'd delete the OPFS
      // session and lose the recording data.
      debug.warn('Recording stopped with no audio data — resetting to idle');
      clearRecording(true);
      setIsRecordingStopped(false);
    }
  }, [
    isRecordingStopped,
    isRecordingCanceled,
    mediaBlobUrl,
    recorderStatus,
    isReconstructingBlob,
  ]);

  return (
    <div
      className={`vai-flex vai-flex-col vai-items-center vai-w-full ${
        isRecordingSessionActive && isSheetOpen
          ? 'vai-h-full vai-min-h-0'
          : 'vai-flex-1 vai-min-h-0 vai-space-y-4'
      }`}
    >
      {!hasMicrophonePermission && !externalPermissionFlow && (
        <MicrophonePermissionFlow
          {...microphonePermissionFlowProps}
          onAudioFileSelected={handleAudioFileSelected}
          isAudioUploadEnabled={isAudioUploadEnabled}
          showDevTools={showDevTools}
          onPermissionOutcome={onPermissionOutcome}
        />
      )}

      {hasMicrophonePermission && (
        <>
          {recordingBlockedByOtherTab && (
            <div
              className="vai-w-full vai-px-4 vai-py-3 vai-bg-amber-50 vai-border vai-border-amber-200 vai-rounded-md vai-text-sm vai-text-amber-800"
              data-testid="recording-blocked-banner"
            >
              <div className="vai-flex vai-items-start vai-gap-2">
                <FontAwesomeIcon
                  icon={faExclamationTriangle}
                  className="vai-text-amber-500 vai-mt-0.5 vai-flex-shrink-0"
                />
                <p>
                  A recording is already in progress in another tab. Please
                  close it or generate that note before starting a new one.
                </p>
              </div>
            </div>
          )}
          {/* Recorder visualizer - fixed at top. When showVisualizer is false (template selection), VoiceCard is still mounted but hidden so it keeps running audio-activity detection for status/dropdown. */}
          <div
            className={
              showVisualizer
                ? 'vai-flex vai-items-center vai-justify-center vai-flex-shrink-0'
                : 'vai-absolute vai-opacity-0 vai-pointer-events-none vai-w-0 vai-h-0 vai-overflow-hidden'
            }
          >
            <div
              className={`vai-flex vai-items-center vai-gap-1 vai-px-4 vai-py-4 ${
                isRecordingSessionActive && isSheetOpen ? 'vai-w-full' : ''
              }`}
            >
              {/* Timer Display - only show when recording */}
              {isRecordingSessionActive && isSheetOpen && (
                <div className="vai-w-1/2 vai-flex vai-justify-center vai-pr-4">
                  <TimerDisplay />
                </div>
              )}
              {/* Microphone Visualizer (always mounted so audio activity detection runs; hidden when showVisualizer is false) */}
              <div className="vai-flex vai-items-center">
                <VoiceCard
                  stream={monitoringStream || previewAudioStream}
                  isRecordingSessionActive={isRecordingSessionActive}
                  {...voiceCardProps}
                />
              </div>
            </div>
          </div>

          {/* Flex container for notice, instructions, and buttons when recording */}
          {isRecordingSessionActive && isSheetOpen ? (
            <div className="vai-flex vai-flex-col vai-flex-1 vai-min-h-0 vai-w-full vai-items-center">
              {/* Scrollable middle: notice + instructions, or full-takeover silence warning */}
              <div className="vai-flex vai-flex-col vai-flex-1 vai-min-h-0 vai-w-full vai-items-center vai-gap-6 vai-overflow-auto vai-px-4">
                {silenceSeverity !== 'none' &&
                !isCancelRecordingConfirmationShown &&
                !isRecordingSessionPaused ? (
                  /* Full-takeover silence warning — replaces notice + instructions */
                  <div
                    className="vai-flex vai-flex-col vai-items-center vai-justify-center vai-flex-1 vai-w-full vai-gap-3"
                    data-testid="silence-takeover"
                  >
                    <FontAwesomeIcon
                      icon={faExclamationTriangle}
                      className="vai-text-amber-400 vai-h-12 vai-w-12"
                    />
                    <p className="vai-text-xl vai-font-bold vai-text-center">
                      {silenceSeverity === 'early'
                        ? "We\u2019re not picking up any audio"
                        : "You\u2019ve been quiet for a bit"}
                    </p>
                    <p className="vai-text-sm vai-text-muted-foreground vai-text-center vai-px-4">
                      {silenceSeverity === 'early'
                        ? 'Make sure your mic is unmuted and connected \u2013 then you\u2019re all set.'
                        : "Your recording is still running. End it when you\u2019re ready, or keep speaking if you\u2019re not done."}
                    </p>
                  </div>
                ) : (
                  <>
                    {/* Notice text */}
                    <div className="vai-flex vai-flex-col vai-items-center vai-flex-shrink-0">
                      <p
                        className="vai-text-xl vai-font-bold"
                        data-testid="recording-status-text"
                      >
                        {displayNoticeText()}
                      </p>
                      <p
                        className="vai-text-sm vai-text-muted-foreground vai-pt-2 vai-text-center vai-px-4"
                        data-testid="recording-details-text"
                      >
                        {displayNoticeDetailsText()}
                      </p>
                    </div>

                    {templateUserInstructions && templateName && (
                      <UserInstructions
                        templateName={templateName}
                        userInstructions={templateUserInstructions}
                        wrapperClassName={userInstructionsWrapperClassName}
                        contentClassName={userInstructionsContentClassName}
                      />
                    )}
                  </>
                )}
              </div>

              {/* Sticky footer: action buttons */}
              <SheetFooter className="vai-items-center vai-justify-center">
                <div className="vai-flex vai-space-x-3">
                  {!isCancelRecordingConfirmationShown && (
                    <>
                      <Button
                        variant="sheet"
                        onClick={handleCancelButtonClick}
                        size={isSheetOpen ? 'default' : 'sm'}
                        disabled={isCancelRecordingConfirmationShown}
                        className="!vai-text-slate-700 hover:!vai-bg-slate-100"
                        data-testid="cancel-recording-button"
                      >
                        <FontAwesomeIcon icon={faXmark} />
                        Cancel
                      </Button>

                      {isRecordingSessionPaused ? (
                        <Button
                          variant="sheet"
                          onClick={handleResumeRecording}
                          className="!vai-text-slate-700 !vai-bg-white hover:!vai-bg-slate-50 vai-border vai-border-slate-300"
                          size={isSheetOpen ? 'default' : 'sm'}
                        >
                          <FontAwesomeIcon icon={faPlay} />
                          Resume
                        </Button>
                      ) : (
                        <Button
                          variant="sheet"
                          onClick={handlePauseRecording}
                          className="!vai-text-slate-700 !vai-bg-white hover:!vai-bg-slate-50 vai-border vai-border-slate-300"
                          size={isSheetOpen ? 'default' : 'sm'}
                          data-testid="pause-recording-button"
                        >
                          <FontAwesomeIcon icon={faPause} />
                          Pause
                        </Button>
                      )}
                      <Button
                        onClick={handleStopRecording}
                        variant={'pulse'}
                        size={isSheetOpen ? 'default' : 'sm'}
                        className="!vai-text-white !vai-bg-blue-600 hover:!vai-bg-blue-700 vai-border vai-border-blue-700"
                        data-testid="generate-note-button"
                      >
                        <span>
                          <FontAwesomeIcon icon={faSparkles} />
                        </span>
                        Generate Note
                      </Button>
                    </>
                  )}

                  {/* Cancel confirmation appears at bottom when triggered */}
                  {isCancelRecordingConfirmationShown && (
                    <>
                      <Button
                        variant="destructive"
                        onClick={handleCancelRecordingConfirmationButtonClick}
                        className="vai-px-6 !vai-bg-red-600 hover:!vai-bg-red-700 !vai-text-white"
                      >
                        Cancel Recording
                      </Button>
                      <Button
                        variant="outline"
                        onClick={handleNotNowButtonClick}
                        className="vai-px-6 !vai-text-slate-700 !vai-bg-white vai-border vai-border-slate-300"
                      >
                        Not Now
                      </Button>
                    </>
                  )}
                </div>
              </SheetFooter>
            </div>
          ) : (
            <div className="vai-flex vai-flex-col vai-w-full vai-flex-1 vai-min-h-0">
              {!isRecordingSessionActive ? (
                <ConsentFlow
                  onConsentDenied={handleConsentDeniedClick}
                  onStartProviderOnlyRecording={
                    handleStartProviderOnlyRecording
                  }
                  onStartRecording={handleStartRecording}
                  onAudioFileSelected={handleAudioFileSelected}
                  isTemplateSelected={isTemplateSelected}
                  monitoringStream={monitoringStream}
                  mediaBlobUrl={mediaBlobUrl}
                  isSheetOpen={isSheetOpen}
                  buttonStyling={buttonStyling}
                  isVoiceNotesConsentEnabled={isVoiceNotesConsentEnabled}
                  isAudioUploadEnabled={isAudioUploadEnabled}
                  showDevTools={showDevTools}
                  compactMode={compactMode}
                />
              ) : null}
            </div>
          )}
        </>
      )}
    </div>
  );
};
